import React, { memo, useMemo, useEffect, useState } from 'react';
import cn from 'classnames';
import useSWRInfinite from 'swr/infinite';
import queryString from 'query-string';
import { useRecoilValue } from 'recoil';

import { IconButton, Link, Spinner } from 'components';
import { Row } from 'components/layout';
import { Button, Input } from 'components/form';
import { ButtonV3 } from 'components/ComponentV2';
import Avatar from 'components/Avatar';
import { Tooltip } from 'components/Tooltip/Tooltip.component';
import { useCandidate } from 'hooks/useCandidate';
import { useNotification } from 'hooks/useNotification';
import { httpHeadersState } from 'lib/atoms/userSecretAtom';
import { get } from 'lib/utils/http';
import { API_ROUTES } from 'lib/api-routes';
import { parsePaginatedResponse } from 'lib/utils/parser';
import {
  StudentSocietiesResponse,
  StudentSocietyAttributes,
} from 'lib/models/student-society';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';

import styles from '../Section.module.scss';

const PER_PAGE_COUNT = 5;

export interface AffiliatedSocietiesProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
}

export const AffiliatedSocieties: React.FC<AffiliatedSocietiesProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
  }: AffiliatedSocietiesProps) => {
    const notificationInstance = useNotification();
    const { headers } = useRecoilValue(httpHeadersState);
    const { onSubscribe, onUnsubscribe, hideAffiliatedSocietiesSection } =
      useCandidate();
    const { candidate } = useAuth();

    const { show_affiliated_societies, email } = candidate || {};

    const [societies, setSocieties] = useState<
      Array<StudentSocietyAttributes> | undefined
    >(undefined);
    const [search, setSearch] = useState('');
    const [hideSection, setHideSection] = useState(false);

    const endOfPageRef = React.useRef<HTMLDivElement | null>(null);
    const scrollToBottom = () => {
      endOfPageRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    };

    const getSocietyListKey = (pageIndex: number) => {
      const params = {
        page: pageIndex + 1,
        per_page: PER_PAGE_COUNT,
        onboarded: search ? undefined : true,
        candidate_university: search ? undefined : true,
        exclude_subscribed: true,
        q: search,
      };
      const qs = queryString.stringify(params, {
        arrayFormat: 'bracket',
        skipEmptyString: true,
      });

      const pathname = `${API_ROUTES.STUDENT_SOCIETIES}?${qs}`;

      return [pathname, headers];
    };

    const {
      data: societiesResponse,
      error: societiesError,
      size,
      setSize,
      mutate,
    } = useSWRInfinite<StudentSocietiesResponse>(getSocietyListKey, get, {
      revalidateOnFocus: false,
      revalidateFirstPage: false,
    });

    const isAPILoading = !societiesResponse && !societiesError;
    const isLoadingMore =
      isAPILoading ||
      (size > 0 &&
        societiesResponse &&
        typeof societiesResponse[size - 1] === 'undefined');

    useEffect(() => {
      const societies =
        parsePaginatedResponse<StudentSocietyAttributes>(societiesResponse);
      setSocieties(societies);
    }, [societiesResponse]);

    const totalCount = societiesResponse
      ? societiesResponse[0]?.meta?.total
      : undefined;

    const hasMoreRecords = useMemo(() => {
      const firstApplicationList = Array.isArray(societiesResponse)
        ? societiesResponse[0]
        : null;
      if (firstApplicationList) {
        return (firstApplicationList?.meta?.total || 0) > size * PER_PAGE_COUNT;
      } else {
        return false;
      }
    }, [societiesResponse, size]);

    useEffect(() => {
      if (!isLoadingMore && size > 1) {
        scrollToBottom();
      }
    }, [isLoadingMore, size]);

    useEffect(() => {
      setSize(1);

      return () => {
        endOfPageRef.current = null;
        setSize(1);
      };
    }, []);

    const contentClassNames = cn(
      {
        [styles.container]: true,
      },
      { [styles.section]: true },
      { [styles.affiliateSocieties]: true },
      {
        [styles.hide]:
          ((totalCount === undefined || totalCount === 0) && !search) ||
          hideSection ||
          !show_affiliated_societies,
      },
      className
    );

    const removeAffiliateSocietiesBanner = async () => {
      setHideSection(true);
      await hideAffiliatedSocietiesSection({
        headers,
        show_affiliated_societies: false,
      });
    };

    const onSubscribeSocietyHandler = async (
      id: string,
      societyName: string,
      isApprovalRequired: boolean
    ) => {
      if (societies) {
        const subscribedSocietyPosition = societies.findIndex(
          (society) => society.id === id
        );
        const modifiedSociety = societies.find((society) => society.id === id);

        if (modifiedSociety) {
          const modifiedList = [
            ...societies.slice(0, subscribedSocietyPosition),
            { ...modifiedSociety, subscribed: true },
            ...societies.slice(subscribedSocietyPosition + 1, societies.length),
          ];
          setSocieties(modifiedList);
        }
        try {
          const response = await onSubscribe({
            email: email || '',
            student_society_id: id,
          });
          if (response) {
            const title = isApprovalRequired
              ? 'Membership Request Sent'
              : `You’ve joined ${societyName}`;
            const message = '';
            notificationInstance.success({
              title,
              message,
            });
          }
        } catch (error) {
          notificationInstance.handleExceptionError(error);
        }
      }
    };
    const onUnsubscribeSocietyHandler = async (id: string) => {
      if (societies) {
        const subscribedSocietyPosition = societies.findIndex(
          (society) => society.id === id
        );
        const modifiedSociety = societies.find((society) => society.id === id);

        if (modifiedSociety) {
          const modifiedList = [
            ...societies.slice(0, subscribedSocietyPosition),
            { ...modifiedSociety, subscribed: false },
            ...societies.slice(subscribedSocietyPosition + 1, societies.length),
          ];
          setSocieties(modifiedList);
        }
        try {
          await onUnsubscribe(headers, id);
        } catch (error) {
          notificationInstance.handleExceptionError(error);
        }
      }
    };

    const getSocietiesList = () => {
      if (societies) {
        return societies.map((society) => {
          const {
            id,
            name,
            icon_url,
            subscribed,
            slug,
            subscribers_require_approval,
          } = society;
          return (
            <Row
              align="center"
              justify="space-between"
              columnGap={12}
              key={id}
              wrap={false}
              className={styles.societiesRow}
            >
              <Row align="center" columnGap={12} wrap={false}>
                <Avatar name={name} icon={icon_url} size="small" />
                <Link
                  href={`${PAGE_ROUTES.SOCIETIES}/${slug}`}
                  underlineOnHover={false}
                  target={'_blank'}
                >
                  <p className={styles.name}>{name}</p>
                </Link>
              </Row>
              {subscribed ? (
                <Tooltip
                  content={
                    subscribers_require_approval
                      ? 'Cancel request'
                      : 'You’re member. Click to leave'
                  }
                  position="top"
                  color="dark"
                  className={styles.tooltip}
                >
                  <ButtonV3
                    className={styles.joinedButton}
                    size="small"
                    color="secondary"
                    startIcon={
                      subscribers_require_approval
                        ? 'icon_check'
                        : 'icon_check-double'
                    }
                    onClick={() => onUnsubscribeSocietyHandler(id)}
                  >
                    {subscribers_require_approval ? 'Request sent' : 'Member'}
                  </ButtonV3>
                </Tooltip>
              ) : (
                <ButtonV3
                  className={styles.joinButton}
                  size="small"
                  color="secondary"
                  onClick={() =>
                    onSubscribeSocietyHandler(
                      id,
                      name,
                      subscribers_require_approval
                    )
                  }
                >
                  Join
                </ButtonV3>
              )}
            </Row>
          );
        });
      }
    };

    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid="AffiliatedSocieties"
        ref={endOfPageRef}
        id="AffiliatedSocieties"
      >
        <IconButton
          iconName="close"
          size="auto"
          className={styles.closeIcon}
          onClick={removeAffiliateSocietiesBanner}
        />
        <p className={styles.title}>
          Are you affiliated with these communities?
        </p>
        <p className={styles.desc}>
          Select communities you’re affiliated with from your university to get
          more discovery by recruiters looking to hire from these communities
          and event updates.
        </p>
        <Row
          align="center"
          columnGap={12}
          isFullWidthRow
          className={styles.searchRow}
        >
          <Input
            inputClassName={cn(styles.search)}
            className={cn(styles.searchBox)}
            placeholder="Search"
            startIcon="icon_search"
            startIconSize="small"
            startIconClassName={styles.searchIcon}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {search ? (
            <ButtonV3
              size="large"
              color="blue"
              variant="text"
              onClick={() => setSearch('')}
              className={styles.cancel}
            >
              Cancel
            </ButtonV3>
          ) : null}
        </Row>
        {totalCount === undefined ? null : (
          <div
            className={cn(styles.societiesList, {
              [styles.empty]: totalCount === 0,
            })}
          >
            {totalCount === 0 ? (
              <div className={styles.societiesListEmpty}>
                <p className={styles.emoji}>🙁</p>
                <p className={styles.text}>No results</p>
              </div>
            ) : (
              getSocietiesList()
            )}
          </div>
        )}
        {totalCount === undefined || isLoadingMore ? (
          <Spinner
            size="small"
            className={cn(styles.spinner)}
            wrapper={totalCount === undefined ? true : false}
            wrapperClassname={cn({
              [styles['min-height']]: totalCount === undefined,
            })}
          />
        ) : null}
        <Row align="center" columnGap={8} className={styles.countsRow}>
          {societies?.length ? (
            <p>
              Showing {societies?.length} of {totalCount}
            </p>
          ) : null}
          {hasMoreRecords ? (
            <Button
              variant="text"
              color="blue"
              onClick={() => {
                setSize(size + 1);
                mutate();
              }}
              className={styles.countsButton}
            >
              Show more
            </Button>
          ) : null}
        </Row>
      </div>
    );
  }
);

AffiliatedSocieties.displayName = 'AffiliatedSocieties';
